import React from "react";
import {
  Box,
  Typography,
  Grid,
  Chip,
  Card,
  CardContent,
  Button,
} from "@mui/material";
import { Edit as EditIcon } from "@mui/icons-material";
import { format } from "date-fns";

const LicenseDetails = ({ license, onEdit }) => {
  const getStatusColor = (status) => {
    switch (status) {
      case "active":
        return "success";
      case "expired":
        return "error";
      case "cancelled":
        return "default";
      case "pending":
        return "warning";
      default:
        return "default";
    }
  };

  const formatCurrency = (amount, currency) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency || "USD",
    }).format(amount);
  };

  return (
    <Box>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Typography variant="h5">License Details</Typography>
        <Button variant="contained" startIcon={<EditIcon />} onClick={onEdit}>
          Edit License
        </Button>
      </Box>

      <Grid container spacing={3}>
        {/* Basic Information */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Basic Information
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography color="textSecondary" variant="body2">
                    Name
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {license.name}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography color="textSecondary" variant="body2">
                    Supplier Name
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {license.vendor}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography color="textSecondary" variant="body2">
                    Category
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {license.category_name ? (
                      <Chip
                        label={license.category_name}
                        size="small"
                        sx={{
                          backgroundColor: license.category_color || "#e0e0e0",
                          color: "white",
                        }}
                      />
                    ) : (
                      "Uncategorized"
                    )}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography color="textSecondary" variant="body2">
                    Status
                  </Typography>
                  <Chip
                    label={license.status}
                    color={getStatusColor(license.status)}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography color="textSecondary" variant="body2">
                    License Key
                  </Typography>
                  <Typography
                    variant="body1"
                    fontFamily="monospace"
                    gutterBottom
                  >
                    {license.license_key || "Not provided"}
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Billing Information */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Billing Information
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography color="textSecondary" variant="body2">
                    License Type
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {license.license_type.charAt(0).toUpperCase() +
                      license.license_type.slice(1)}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography color="textSecondary" variant="body2">
                    Billing Frequency
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {license.billing_frequency.charAt(0).toUpperCase() +
                      license.billing_frequency.slice(1)}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography color="textSecondary" variant="body2">
                    Billing Amount
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {formatCurrency(license.billing_amount, license.currency)}
                    {license.includes_vat && (
                      <Box mt={1}>
                        <Typography
                          variant="caption"
                          color="textSecondary"
                          display="block"
                        >
                          Net:{" "}
                          {formatCurrency(license.net_amount, license.currency)}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="textSecondary"
                          display="block"
                        >
                          VAT ({license.vat_rate}%):{" "}
                          {formatCurrency(license.vat_amount, license.currency)}
                        </Typography>
                      </Box>
                    )}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography color="textSecondary" variant="body2">
                    Auto Renewal
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {license.auto_renewal ? "Yes" : "No"}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography color="textSecondary" variant="body2">
                    User Allocation
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    <Chip
                      label={`${license.user_allocation || 1} users`}
                      size="small"
                      variant="outlined"
                      color="primary"
                    />
                  </Typography>
                </Grid>
                {license.auto_renewal && license.renewal_amount && (
                  <Grid item xs={6}>
                    <Typography color="textSecondary" variant="body2">
                      Renewal Amount
                    </Typography>
                    <Typography variant="body1" gutterBottom>
                      {formatCurrency(
                        license.renewal_amount,
                        license.renewal_currency,
                      )}
                      {license.renewal_includes_vat && (
                        <Box mt={1}>
                          <Typography
                            variant="caption"
                            color="textSecondary"
                            display="block"
                          >
                            Net:{" "}
                            {formatCurrency(
                              license.renewal_net_amount,
                              license.renewal_currency,
                            )}
                          </Typography>
                          <Typography
                            variant="caption"
                            color="textSecondary"
                            display="block"
                          >
                            VAT ({license.vat_rate}%):{" "}
                            {formatCurrency(
                              license.renewal_vat_amount,
                              license.renewal_currency,
                            )}
                          </Typography>
                        </Box>
                      )}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Dates */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Important Dates
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography color="textSecondary" variant="body2">
                    Start Date
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {format(new Date(license.start_date), "dd MMM yyyy")}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography color="textSecondary" variant="body2">
                    Expiry Date
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {format(new Date(license.expiry_date), "dd MMM yyyy")}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography color="textSecondary" variant="body2">
                    Created
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {format(new Date(license.created_at), "dd MMM yyyy HH:mm")}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography color="textSecondary" variant="body2">
                    Last Updated
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {format(new Date(license.updated_at), "dd MMM yyyy HH:mm")}
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Additional Information */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Additional Information
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography color="textSecondary" variant="body2">
                    Created By
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {license.created_by_name || "Unknown"}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography color="textSecondary" variant="body2">
                    Notes
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {license.notes || "No notes available"}
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Reminders */}
        {license.reminders && license.reminders.length > 0 && (
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Reminders
                </Typography>
                <Grid container spacing={2}>
                  {license.reminders.map((reminder, index) => (
                    <Grid item xs={12} sm={6} md={4} key={reminder.id}>
                      <Box
                        sx={{
                          p: 2,
                          border: "1px solid",
                          borderColor: "divider",
                          borderRadius: 1,
                        }}
                      >
                        <Typography variant="body2" color="textSecondary">
                          {reminder.reminder_type.charAt(0).toUpperCase() +
                            reminder.reminder_type.slice(1)}{" "}
                          Reminder
                        </Typography>
                        <Typography variant="body1">
                          {reminder.reminder_days} days before expiry
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          Status: {reminder.is_active ? "Active" : "Inactive"}
                        </Typography>
                        {reminder.last_sent && (
                          <Typography variant="body2" color="textSecondary">
                            Last sent:{" "}
                            {format(
                              new Date(reminder.last_sent),
                              "dd MMM yyyy",
                            )}
                          </Typography>
                        )}
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

export default LicenseDetails;
